import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule } from '@ngx-translate/core';
import { RxFor } from '@rx-angular/template/for';
import {
  AbsencesModule,
  GetAbsenceTimelineCardTitlePipe,
} from '@wilson/absences';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  Absence,
  RoleAction,
  RolePermissionSubject,
  StaffLayoutStyle,
} from '@wilson/interfaces';
import {
  ACTION_TOASTER_DURATION,
  NzMessageTimerLineComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-message';
import {
  DeferredAbsenceTimelineViewCardHelperService,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
  MessageWithUndoOptionComponent,
  ShiftMultiSelectHelperService,
  TimeLineAbsenceTooltipComponent,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { ShiftTimelinePipesModule } from '@wilson/shift-timeline/pipes';
import {
  ChangedKeyManagerService,
  DragAndDropModeService,
  PreferredSettingsStateFacadeService,
  TimelineHelperService,
} from '@wilson/shift-timeline/services';
import { NzElementPatchModule } from 'ng-zorro-antd/core/element-patch';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Observable } from 'rxjs';
import { MoveAbsenceDropZoneHelperService } from '../move-absence-drop-zone/move-absence-drop-zone-helper.service';
import { AbsenceDrawerHelperService } from '@wilson/non-domain-specific/absence';

@Component({
  selector: 'wilson-timeline-user-absence',
  templateUrl: './timeline-user-absence.component.html',
  styleUrls: ['./timeline-user-absence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FeatureFlagsModule,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    ShiftTimelinePipesModule,
    NzTooltipModule,
    AbsencesModule,
    TranslateModule,
    TimeLineAbsenceTooltipComponent,
    NzElementPatchModule,
    MessageWithUndoOptionComponent,
    NzMessageTimerLineComponent,
    GetAbsenceTimelineCardTitlePipe,
    AuthorizationModule,
    RxFor,
    NzElementPatchModule,
  ],
})
export class TimelineUserAbsenceComponent implements OnInit {
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly changedKeyManagerService = inject(ChangedKeyManagerService);
  private readonly absenceDrawerHelperService = inject(
    AbsenceDrawerHelperService,
  );
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  readonly moveAbsenceDropZoneHelperService = inject(
    MoveAbsenceDropZoneHelperService,
  );
  protected readonly timelineHelperService = inject(TimelineHelperService);
  protected readonly deferredAbsenceTimelineViewCardHelperService = inject(
    DeferredAbsenceTimelineViewCardHelperService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );

  @Input() absences!: Absence[];
  @Input() loadingAbsenceIds: string[] = [];
  @Input() isOverlapDetected = false;
  @Input() draggingAsenceId: string | null = null;
  @Input() absenceHeight = 0;
  @Output() openDrawer = new EventEmitter();

  isTooltipVisible = true;
  isCardVisible = true;
  staffLayoutStyle$!: Observable<StaffLayoutStyle>;
  StaffLayoutStyle = StaffLayoutStyle;

  public readonly MESSAGE_DURATION = ACTION_TOASTER_DURATION;

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  trackByAbsenceDetail: TrackByFunction<Absence> = (
    _: number,
    absence: Absence,
  ) => {
    return this.changedKeyManagerService.computeChangedKeyForAbsence(absence);
  };
  @ViewChild('cardContainer', { static: false })
  cardContainer!: ElementRef<HTMLDivElement>;

  ngOnInit(): void {
    this.staffLayoutStyle$ =
      this.preferredSettingsStateFacadeService.staffLayoutStyle$;
  }

  public startDrag(
    event: DragEvent,
    left: number | null,
    right: number | null,
    absence: Absence,
    previewCard: Element,
    hasAbsenceUpdatePermissions: boolean,
  ): void {
    if (hasAbsenceUpdatePermissions) {
      this.isTooltipVisible = false;
      const isDragStarted =
        this.moveAbsenceDropZoneHelperService.startDrag(absence);
      if (isDragStarted) {
        this.initiateDragAndDrop(event, left, right, previewCard);
      }
    } else {
      this.timelineHelperService.informUserHasNoPermissionsToMoveEntity();
    }
  }

  public dragEnd(isMoveMode: boolean): void {
    this.isTooltipVisible = true;
    this.moveAbsenceDropZoneHelperService.dragEnd(isMoveMode);
    this.isCardVisible = true;
    this.cdRef.detectChanges();
  }

  private initiateDragAndDrop(
    event: DragEvent,
    left: number | null,
    right: number | null,
    previewCard: Element,
  ): void {
    event.dataTransfer?.setDragImage(previewCard, 0, 0);
    setTimeout(() => {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      this.isCardVisible = false;
      this.moveAbsenceDropZoneHelperService.initiateDragAndDrop(left, right);
      this.cdRef.detectChanges();
    });
  }

  trackHorizontalDrag(
    event: DragEvent,
    left: number | null,
    right: number | null,
    shiftDuration: number | null,
  ): void {
    if (left && right) {
      const offsetInPixels = (event as DragEvent & { layerX: number }).layerX;
      const trackedLeftPosition = left + offsetInPixels;
      const trackedRightPosition = right - offsetInPixels;

      const range = {
        left:
          trackedLeftPosition -
          DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        right:
          trackedRightPosition +
          DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL,
        shiftDuration: shiftDuration,
        adjustmentNeeded: true,
      };
      this.dragAndDropModeService.setHorizontalDemarcatorRange(range);
    }
  }

  selectAbsence(
    data: {
      id: string | undefined;
      left: number | null;
      right: number | null;
    },
    undoTemplate: TemplateRef<void>,
  ): void {
    this.shiftMultiSelectHelperService.clearSelectionWithUndo(
      undoTemplate,
      this.MESSAGE_DURATION,
    );
    this.openDrawer.emit({
      left: data.left,
      right: data.right,
      id: data.id,
    });
  }

  onUndo(): void {
    this.absenceDrawerHelperService.toggleAbsenceDrawer(false);
  }
}
